import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MapPin, Users, Star, Calendar, ArrowRight, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ExperienceCard, ExperienceCardSkeleton } from '@/components/ExperienceCard';
import { experiencesApi, explorersApi } from '@/api';

// Destination data with SEO content
const DESTINATIONS = {
  fribourg: {
    name: 'Fribourg',
    country: 'Suisse',
    heroImage: 'https://images.unsplash.com/photo-1573108724029-4c46571d6490?w=1920&q=80',
    description: 'Découvrez Fribourg avec des guides locaux passionnés. Ville médiévale bilingue au cœur de la Suisse, Fribourg offre un mélange unique de culture, gastronomie et nature.',
    highlights: ['Vieille ville médiévale', 'Ponts suspendus', 'Fondue fribourgeoise', 'Lac de la Gruyère'],
    seoTitle: 'Guide Local Fribourg | Expériences Authentiques',
    seoDescription: 'Explorez Fribourg avec un guide local. Visites de la vieille ville, dégustation de fondue, randonnées dans les Préalpes. Réservez votre expérience unique.'
  },
  lausanne: {
    name: 'Lausanne',
    country: 'Suisse',
    heroImage: 'https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?w=1920&q=80',
    description: 'Explorez Lausanne, capitale olympique au bord du Lac Léman. Nos guides locaux vous font découvrir les trésors cachés de cette ville dynamique.',
    highlights: ['Cathédrale de Lausanne', 'Musée Olympique', 'Vignobles de Lavaux', 'Ouchy et le lac'],
    seoTitle: 'Guide Local Lausanne | Visites & Expériences',
    seoDescription: 'Découvrez Lausanne autrement avec un guide local. Visites culturelles, dégustation de vins de Lavaux, balades au bord du lac. Expériences authentiques.'
  },
  geneve: {
    name: 'Genève',
    country: 'Suisse',
    heroImage: 'https://images.unsplash.com/photo-1573108037329-37aa135a142e?w=1920&q=80',
    description: 'Vivez Genève comme un local. Ville internationale par excellence, Genève cache des quartiers authentiques et des expériences uniques à découvrir.',
    highlights: ['Jet d\'eau', 'Vieille ville', 'CERN', 'Carouge'],
    seoTitle: 'Guide Local Genève | Découverte Authentique',
    seoDescription: 'Explorez Genève avec un guide local passionné. Visites hors des sentiers battus, quartiers cachés, gastronomie locale. Réservez maintenant.'
  },
  zurich: {
    name: 'Zurich',
    country: 'Suisse',
    heroImage: 'https://images.unsplash.com/photo-1515488764276-beab7607c1e6?w=1920&q=80',
    description: 'Découvrez Zurich au-delà des clichés bancaires. Une ville vibrante avec une scène culturelle riche et des quartiers pleins de caractère.',
    highlights: ['Vieille ville', 'Lac de Zurich', 'Kunsthaus', 'Quartier Kreis 4 & 5'],
    seoTitle: 'Guide Local Zurich | Expériences Locales',
    seoDescription: 'Zurich authentique avec un guide local. Art, culture, vie nocturne, nature. Découvrez les secrets de la plus grande ville suisse.'
  },
  berne: {
    name: 'Berne',
    country: 'Suisse',
    heroImage: 'https://images.unsplash.com/photo-1587974928442-77dc3e0dba72?w=1920&q=80',
    description: 'Explorez la capitale suisse avec des guides passionnés. Berne, classée au patrimoine mondial de l\'UNESCO, regorge de trésors à découvrir.',
    highlights: ['Vieille ville UNESCO', 'Fosse aux ours', 'Zytglogge', 'Gurten'],
    seoTitle: 'Guide Local Berne | Capitale Suisse',
    seoDescription: 'Berne avec un guide local. Découvrez la capitale suisse, son centre historique UNESCO, sa culture et ses traditions. Expériences uniques.'
  }
};

const DestinationPage = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [experiences, setExperiences] = useState([]);
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  const destination = DESTINATIONS[slug] || DESTINATIONS.fribourg;

  useEffect(() => {
    // Update page title and meta for SEO
    document.title = destination.seoTitle;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', destination.seoDescription);
    }

    fetchData();
  }, [slug]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch experiences for this location
      const expData = await experiencesApi.search({ location: destination.name });
      setExperiences(expData.slice(0, 6));

      // Fetch guides for this location
      const guidesData = await explorersApi.getAll();
      const localGuides = guidesData.filter(g => 
        g.location?.toLowerCase().includes(destination.name.toLowerCase())
      ).slice(0, 4);
      setGuides(localGuides);
    } catch (error) {
      console.error('Error fetching destination data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-[50vh] md:h-[60vh] overflow-hidden">
        <img
          src={destination.heroImage}
          alt={`${destination.name}, ${destination.country}`}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-6xl mx-auto"
          >
            <div className="flex items-center gap-2 text-white/80 mb-2">
              <MapPin className="w-5 h-5" />
              <span>{destination.country}</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white font-serif mb-4">
              {destination.name}
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl">
              {destination.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-8 bg-primary/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap gap-3 justify-center">
            {destination.highlights.map((highlight, index) => (
              <motion.span
                key={highlight}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="px-4 py-2 bg-white rounded-full text-sm font-medium text-gray-700 shadow-sm"
              >
                {highlight}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* Experiences Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold">
                {i18n.language === 'fr' ? `Expériences à ${destination.name}` : `Experiences in ${destination.name}`}
              </h2>
              <p className="text-gray-600 mt-1">
                {i18n.language === 'fr' 
                  ? 'Découvrez des activités uniques avec des guides locaux' 
                  : 'Discover unique activities with local guides'}
              </p>
            </div>
            <Link to={`/explore?location=${destination.name}`}>
              <Button variant="outline" className="hidden md:flex">
                {i18n.language === 'fr' ? 'Voir tout' : 'View all'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => <ExperienceCardSkeleton key={i} />)}
            </div>
          ) : experiences.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {experiences.map((exp, index) => (
                <motion.div
                  key={exp.experience_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <ExperienceCard experience={exp} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-2xl">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                {i18n.language === 'fr' 
                  ? 'Bientôt des expériences ici !' 
                  : 'Experiences coming soon!'}
              </h3>
              <p className="text-gray-500 mb-4">
                {i18n.language === 'fr'
                  ? `Nos guides de ${destination.name} préparent des expériences uniques pour vous.`
                  : `Our guides in ${destination.name} are preparing unique experiences for you.`}
              </p>
              <Link to="/explore">
                <Button>
                  {i18n.language === 'fr' ? 'Explorer d\'autres destinations' : 'Explore other destinations'}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Guides Section */}
      {guides.length > 0 && (
        <section className="py-12 px-4 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold mb-8">
              {i18n.language === 'fr' ? `Guides locaux à ${destination.name}` : `Local guides in ${destination.name}`}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {guides.map((guide, index) => (
                <motion.div
                  key={guide.user_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/explorer/${guide.user_id}`}>
                    <div className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                      <img
                        src={guide.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(guide.name)}&background=f97316&color=fff`}
                        alt={guide.name}
                        className="w-20 h-20 rounded-full mx-auto mb-3 object-cover"
                      />
                      <h3 className="font-semibold text-center">{guide.name}</h3>
                      {guide.rating > 0 && (
                        <div className="flex items-center justify-center gap-1 mt-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          <span className="text-sm text-gray-600">{guide.rating.toFixed(1)}</span>
                        </div>
                      )}
                      <p className="text-sm text-gray-500 text-center mt-2 line-clamp-2">
                        {guide.bio || (i18n.language === 'fr' ? 'Guide local passionné' : 'Passionate local guide')}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-primary to-orange-500">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {i18n.language === 'fr' 
              ? `Vous connaissez ${destination.name} ?` 
              : `Do you know ${destination.name}?`}
          </h2>
          <p className="text-lg text-white/90 mb-8">
            {i18n.language === 'fr'
              ? 'Devenez guide local et partagez votre passion avec des voyageurs du monde entier.'
              : 'Become a local guide and share your passion with travelers from around the world.'}
          </p>
          <Link to="/register">
            <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-gray-100">
              {i18n.language === 'fr' ? 'Devenir guide' : 'Become a guide'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Other Destinations */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold mb-6">
            {i18n.language === 'fr' ? 'Autres destinations' : 'Other destinations'}
          </h2>
          <div className="flex flex-wrap gap-3">
            {Object.entries(DESTINATIONS)
              .filter(([key]) => key !== slug)
              .map(([key, dest]) => (
                <Link key={key} to={`/destination/${key}`}>
                  <Button variant="outline" className="rounded-full">
                    <MapPin className="w-4 h-4 mr-2" />
                    {dest.name}
                  </Button>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default DestinationPage;
